import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/lib/components/ui/dialog";
import { Button } from "@/lib/components/ui/button";
import { Input } from "@/lib/components/ui/input";
import { Label } from "@/lib/components/ui/label";
import { cn } from "@/lib/utils";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultEmail?: string;
  showScaleOption?: boolean;
  suggestedScale?: number | null;
  originalObb?: number[] | null; // [x, y, z] mm
  submitting?: boolean;
  scaling?: boolean;
  onSubmitReview: (email: string, message: string) => Promise<void> | void;
  onScaleToFit?: () => Promise<void> | void;
};

export default function FurtherInspectionDialog({
  open,
  onOpenChange,
  defaultEmail,
  showScaleOption = false,
  suggestedScale = null,
  originalObb = null,
  submitting = false,
  scaling = false,
  onSubmitReview,
  onScaleToFit,
}: Props) {
  const [email, setEmail] = React.useState(defaultEmail ?? "");
  const [message, setMessage] = React.useState("");

  React.useEffect(() => {
    if (open) {
      setEmail(defaultEmail ?? "");
      setMessage("");
    }
  }, [open, defaultEmail]);

  const scaledDims =
    originalObb && suggestedScale
      ? originalObb.map((d) => d * suggestedScale)
      : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby="further-inspection-description">
        <DialogHeader>
          <DialogTitle>Your model requires manual analysis</DialogTitle>
          <DialogDescription id="further-inspection-description">
            Please provide your email and an optional message. We’ll review your
            model and get back to you shortly.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          {showScaleOption && suggestedScale && originalObb && (
            <div className="rounded-lg border p-3 text-sm">
              <div className="font-medium mb-1">This model doesn’t fit the bed</div>
              <div className="text-muted-foreground">
                Suggested scale: {(suggestedScale * 100).toFixed(0)}%
              </div>
              <div className="text-muted-foreground">
                New dimensions:{" "}
                {scaledDims?.map((d) => d.toFixed(2)).join(" × ")} mm
              </div>
            </div>
          )}

          <div className="grid gap-2">
            <Label htmlFor="email">Your Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="message">Message (optional)</Label>
            <textarea
              id="message"
              placeholder="Anything you'd like to add"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className={cn(
                "file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input min-h-24 rounded-md border bg-transparent px-3 py-2 text-base shadow-xs transition-[color,box-shadow] outline-none md:text-sm",
                "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]"
              )}
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting || scaling}
          >
            Cancel
          </Button>

          {showScaleOption && onScaleToFit && (
            <Button
              variant="secondary"
              onClick={() => onScaleToFit()}
              disabled={scaling || submitting}
            >
              {scaling ? "Scaling…" : "Scale to fit"}
            </Button>
          )}

          <Button
            onClick={() => onSubmitReview(email, message)}
            disabled={submitting || !email}
          >
            {submitting ? "Sending…" : "Send for manual analysis"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
